import {
  Button,
  Card,
  ConfigProvider,
  DatePicker,
  ExportButton,
  Form,
  getDefaultSortOrder,
  Icons,
  Input,
  List,
  Select,
  ShowButton,
  Space,
  Table,
  TextField,
  useSelect,
  useTable,
} from "@pankod/refine-antd";
import {
  CrudFilters,
  HttpError,
  IResourceComponentsProps,
  useExport,
  usePermissions,
} from "@pankod/refine-core";
import type { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import "dayjs/locale/pt-br";

import { IBranchOfficesSearch, IHealthInsurer, ILeads } from "interfaces";
import { useMemo, useState } from "react";
import "./styles.css";

import ptBR from "antd/es/date-picker/locale/pt_BR";
import { DefaultMaskedInput } from "components/extra/default-masked-input";
import {
  budgetWppInegibleReasonMap,
  lastScreenList as SCREEN_LIST,
} from "utils/constants";
import { formatCPF } from "utils/formaters";
import { IPartner } from "interfaces/partner";
import { useInfiniteSelect } from "hooks/useInfiniteSelect";
import { CustomTag } from "components/extra/custom-tag";
import { STATUS_SIGO_LIST } from "enums/sigoStatus";
import { UserRole } from "enums";
import {
  BudgetSearchFormValues,
  BudgetSearchParams,
  FILTER_EQUALS_OPERATOR,
  PAGE_SIZE,
  STATES_LIST_BRAZIL,
  buildExportFilters,
  buildSearchFilters,
  buildSearchParams,
  getBudgetStatusLabel,
  getValidFilters,
  mapBudgetExportData,
} from "./list.helpers";
import { FirstAccessWhatsappResendAction } from "./components/first-access-whatsapp-resend-action";
import { GenerateLoginUrlAction } from "./components/generate-login-url-action";
import { UpdateBudgetStatusAction } from "./components/update-budget-status-action";
import { canUseFirstAccessWhatsappResend } from "./utils/first-access-whatsapp-resend";
import { canUseGenerateLoginUrl } from "./utils/login-url";

dayjs.locale("pt-br"); 

const renderCellText = (value: unknown) => <TextField value={value} />;

const formatDateTime = (value?: string | null) =>
  value ? dayjs(value).format("DD/MM/YYYY HH:mm") : "-";

const renderWhatsapp = (wpp?: ILeads["WhatsApp"]) => {
  if (!wpp) return <TextField value="-" />;

  const reasonConfig = budgetWppInegibleReasonMap[wpp];
  if (!reasonConfig) return <TextField value="-" />;

  return <CustomTag color={reasonConfig.color} label={reasonConfig.label} />;
};

export const BudgetsList: React.FC<IResourceComponentsProps> = () => {
  const permissions = usePermissions();
  const [params, setParams] = useState<BudgetSearchParams>();
  const [canExport, setCanExport] = useState(false);
  const [showData, setShowData] = useState(false);

  const BASIC_ACCESS_PERMISSION = permissions.data === UserRole.ReportsBasic;
  const CAN_RESEND_FIRST_ACCESS_MESSAGE = canUseFirstAccessWhatsappResend(
    permissions.data,
  );
  const CAN_GENERATE_LOGIN_URL = canUseGenerateLoginUrl(permissions.data);

  const { selectProps: selectPropsPartners } = useInfiniteSelect<IPartner>({
    resource: "v1/partners",
    optionLabel: "partner_name",
    optionValue: "partner_code",
    sort: [{ field: "partner_name", order: "asc" }],
    pageSize: 20,
  });

  const partnerOptions =
    selectPropsPartners.options?.map((opt) => ({
      ...opt,
      label: `${opt.label}`,
    })) ?? [];

  const handleSearch = (values: BudgetSearchFormValues): CrudFilters => {
    return buildSearchFilters(values);
  };

  const exportFilters: CrudFilters = buildExportFilters(params);

  const { tableProps, searchFormProps, sorter, tableQueryResult } = useTable<
    ILeads,
    HttpError,
    BudgetSearchFormValues
  >({
    resource: "v2/budget",
    metaData: {
      usePost: true,
      query: {
        customParam: "value",
      },
    },
    initialFilter: [
      {
        field: "created_start",
        operator: FILTER_EQUALS_OPERATOR,
        value: null,
      },
      {
        field: "created_end",
        operator: FILTER_EQUALS_OPERATOR,
        value: null,
      },
    ],
    onSearch: handleSearch,
    initialCurrent: 1,
    initialPageSize: PAGE_SIZE,
    hasPagination: true,
    queryOptions: {
      enabled: showData,
    },
  });

  const columns = useMemo<ColumnsType<ILeads>>(
    () => [
      {
        dataIndex: "_id",
        key: "_id",
        title: "Código do Orçamento",
        render: renderCellText,
        defaultSortOrder: getDefaultSortOrder("_id", sorter),
        sorter: true,
      },
      {
        dataIndex: "cpf",
        key: "cpf",
        title: "CPF",
        render: (value) => renderCellText(formatCPF(value)),
        defaultSortOrder: getDefaultSortOrder("cpf", sorter),
        sorter: true,
      },
      {
        dataIndex: "name",
        key: "name",
        title: "Nome",
        render: renderCellText,
        defaultSortOrder: getDefaultSortOrder("Name", sorter),
        sorter: true,
      },
      {
        dataIndex: "cell_phone",
        key: "cell_phone",
        title: "Telefone",
        render: renderCellText,
        defaultSortOrder: getDefaultSortOrder("cell_phone", sorter),
        sorter: true,
      },
      {
        dataIndex: "email",
        key: "email",
        title: "E-Mail",
        render: renderCellText,
        defaultSortOrder: getDefaultSortOrder("email", sorter),
        sorter: true,
      },
      {
        dataIndex: "health_insurer_name",
        key: "health_insurer_name",
        title: "Operadora",
        render: renderCellText,
        defaultSortOrder: getDefaultSortOrder("health_insurer_name", sorter),
        sorter: true,
      },
      {
        dataIndex: "status",
        key: "status",
        title: "Status SIGO",
        render: (value) => renderCellText(getBudgetStatusLabel(value, "")),
        defaultSortOrder: getDefaultSortOrder("status", sorter),
        sorter: true,
      },
      {
        dataIndex: "city",
        key: "city",
        title: "Cidade",
        render: (value, record) =>
          renderCellText(`${value} - ${record.state}`),
        defaultSortOrder: getDefaultSortOrder("city", sorter),
        sorter: true,
      },
      {
        dataIndex: "control_number",
        key: "control_number",
        title: "Nº de Controle",
        render: renderCellText,
        defaultSortOrder: getDefaultSortOrder("control_number", sorter),
        sorter: true,
      },
      {
        dataIndex: "budget_number",
        key: "budget_number",
        title: "Nº do Orçamento",
        render: renderCellText,
        defaultSortOrder: getDefaultSortOrder("budget_number", sorter),
        sorter: true,
      },
      {
        dataIndex: "product_name",
        key: "product_name",
        title: "Produto",
        render: (value, record) =>
          renderCellText(`${record.product} - ${value}`),
        defaultSortOrder: getDefaultSortOrder("product_name", sorter),
        sorter: true,
      },
      {
        dataIndex: "dependents",
        key: "dependents",
        title: "Vidas",
        render: renderCellText,
        defaultSortOrder: getDefaultSortOrder("dependents", sorter),
        sorter: true,
      },
      {
        dataIndex: "last_screen",
        key: "last_screen",
        title: "Última Tela",
        render: (value) => renderCellText(value || "-"),
        defaultSortOrder: getDefaultSortOrder("last_screen", sorter),
        sorter: true,
      },
      {
        dataIndex: "partner",
        key: "partner_code",
        title: "Identificação do Parceiro",
        render: renderCellText,
        defaultSortOrder: getDefaultSortOrder("partner_code", sorter),
        sorter: true,
      },
      {
        dataIndex: "origin",
        key: "origin",
        title: "Origem",
        render: renderCellText,
        defaultSortOrder: getDefaultSortOrder("origin", sorter),
        sorter: true,
      },
      {
        dataIndex: "created_at",
        key: "created_at",
        title: "Data de Criação",
        render: (value) => renderCellText(formatDateTime(value)),
        defaultSortOrder: getDefaultSortOrder("created_at", sorter),
        sorter: true,
      },
      {
        dataIndex: "updated_at",
        key: "updated_at",
        title: "Data da Última Atualização",
        render: (value) => renderCellText(formatDateTime(value)),
        defaultSortOrder: getDefaultSortOrder("updated_at", sorter),
        sorter: true,
      },
      {
        dataIndex: "payment_details.PaymentDate",
        key: "payment_details.PaymentDate",
        title: "Data de Pgto.",
        render: (_, record) =>
          renderCellText(formatDateTime(record?.payment_details?.PaymentDate)),
        defaultSortOrder: getDefaultSortOrder(
          "payment_details.BillEmissionDate",
          sorter,
        ),
        sorter: true,
      },
      {
        dataIndex: "payment_details.BillEmissionDate",
        key: "payment_details.BillEmissionDate",
        title: "Data da Emissão do Boleto",
        render: (_, record) =>
          renderCellText(
            formatDateTime(record?.payment_details?.BillEmissionDate),
          ),
        defaultSortOrder: getDefaultSortOrder(
          "payment_details.BillEmissionDate",
          sorter,
        ),
        sorter: true,
      },
      {
        dataIndex: "affiliate",
        key: "affiliate",
        title: "Código afiliado",
        render: renderCellText,
        sorter: true,
      },
      {
        dataIndex: "WhatsApp",
        key: "WhatsApp",
        title: "WhatsApp",
        render: renderWhatsapp,
      },
      {
        dataIndex: "EntryUrl",
        key: "EntryUrl",
        title: "URL Completa",
        render: (value: ILeads["EntryUrl"]) => (
          <TextField
            ellipsis
            style={{ width: "140px" }}
            title={value || "-"}
            value={value || "-"}
          />
        ),
        sorter: true,
      },
      {
        dataIndex: "Gclid",
        key: "Gclid",
        title: "Gclid",
        render: (value: ILeads["Gclid"]) => (
          <TextField
            ellipsis
            style={{ width: "140px" }}
            title={value || "-"}
            value={value || "-"}
          />
        ),
      },
      {
        dataIndex: "actions",
        key: "actions",
        title: "Ações",
        render: (_, record) => (
          <Space>
            <GenerateLoginUrlAction
              budgetId={record._id}
              controlNumber={record.control_number}
              name={record.name}
              healthInsurerName={record.health_insurer_name}
              canExecute={CAN_GENERATE_LOGIN_URL}
            />
            <ShowButton
              hidden={BASIC_ACCESS_PERMISSION}
              hideText
              size="small"
              recordItemId={record._id}
            />
            <FirstAccessWhatsappResendAction
              budgetId={record._id}
              status={record.status}
              canExecute={CAN_RESEND_FIRST_ACCESS_MESSAGE}
              onSuccess={tableQueryResult.refetch}
            />
            <UpdateBudgetStatusAction
              controlNumber={record.control_number}
              onSuccess={tableQueryResult.refetch}
            />
          </Space>
        ),
      },
    ],
    [
      BASIC_ACCESS_PERMISSION,
      CAN_GENERATE_LOGIN_URL,
      CAN_RESEND_FIRST_ACCESS_MESSAGE,
      sorter,
      tableQueryResult.refetch,
    ],
  );

  const { triggerExport, isLoading: exportLoading } = useExport<ILeads>({
    resourceName: "v2/budget",
    metaData: {
      usePost: true,
    },
    filters: getValidFilters(exportFilters),
    mapData: mapBudgetExportData,
    pageSize: PAGE_SIZE,
  });

  const { selectProps: selectPropsBranchOffice } =
    useSelect<IBranchOfficesSearch>({
      resource: "v1/branch-office/distinct-ids",
      optionLabel: "branch_id",
      optionValue: "branch_id",
      hasPagination: false,
    });

  const { selectProps: selecthealthInsurer } = useSelect<IHealthInsurer>({
    resource: "v1/health-insurer",
    optionLabel: "Description",
    optionValue: "HealthInsurerId",
    hasPagination: true,
  });

  const handleClearFilters = () => {
    searchFormProps.form?.resetFields();
    setParams(undefined);
    setCanExport(false);
    setShowData(false);
  };

  const handleSubmitForm = () => {
    const form = searchFormProps.form;
    if (!form) return;

    const values = form.getFieldsValue();
    setParams(buildSearchParams(values));
    setCanExport(true);
    setShowData(true);
    form.submit();
  };

  return (
    <List
      headerProps={{
        extra: (
          <Space>
            <ExportButton
              onClick={triggerExport}
              loading={exportLoading}
              disabled={!canExport}
            />
          </Space>
        ),
      }}
    >
      <Card title="Busca">
        <ConfigProvider>
          <Form {...searchFormProps}>
            <div className="filter-container">
              <div className="filters-inputs">
                <div className="filter-fields-group-item">
                  <label>CPF</label>

                  <Form.Item name="cpf">
                    <DefaultMaskedInput
                      mask={"000.000.000-00"}
                      className="input-custom ant-input"
                      placeholder="Digite o CPF"
                    />
                  </Form.Item>
                </div>

                <div className="filter-fields-group-item">
                  <label>Código do Orçamento</label>

                  <Form.Item name="budget_id">
                    <Input
                      className="input-custom ant-input"
                      placeholder="Digite o código do orçamento"
                    />
                  </Form.Item>
                </div>

                <div className="filter-fields-group-item">
                  <label>Telefone</label>
                  <Form.Item name="cell_phone">
                    <DefaultMaskedInput
                      mask="(00)00000-0000"
                      className="input-custom  ant-input"
                      placeholder="Digite o telefone"
                    />
                  </Form.Item>
                </div>

                <div className="filter-fields-group-item">
                  <label>Controle</label>
                  <Form.Item
                    name="ctrl_number"
                    className="filter-fields-group-item"
                  >
                    <Input
                      className="input-custom ant-input"
                      placeholder="Digite o número de controle"
                    />
                  </Form.Item>
                </div>

                <div className="filter-fields-group-item">
                  <label>Produto</label>
                  <Form.Item
                    name="plan_base_code"
                    className="filter-fields-group-item"
                  >
                    <Input
                      className="input-custom"
                      placeholder="Digite o código do produto"
                    />
                  </Form.Item>
                </div>

                <div className="filter-fields-group-item">
                  <label>Código ANS</label>
                  <Form.Item name="ans_code">
                    <Input
                      className="input-custom"
                      placeholder="Digite o código ANS"
                    />
                  </Form.Item>
                </div>

                <div className="filter-fields-group-item">
                  <label>Numero Orçamento</label>
                  <Form.Item name="budget_number">
                    <Input
                      className="input-custom"
                      placeholder="Digite o número do orçamento"
                    />
                  </Form.Item>
                </div>

                <div className="filter-fields-group-item">
                  <div className="group-insurer-branchoffice">
                    <div className="state-city-field">
                      <label>Operadora</label>
                      <Form.Item name="health_insurer" className="input-select">
                        <Select
                          {...selecthealthInsurer}
                          placeholder="Selecione a operadora"
                          options={[
                            { label: "Todas", value: null },
                            ...(selecthealthInsurer.options ?? []),
                          ]}
                        />
                      </Form.Item>
                    </div>
                    <div className="state-city-field">
                      <label>Código da Filial</label>
                      <Form.Item name="branch_code" className="input-select">
                        <Select
                          {...selectPropsBranchOffice}
                          placeholder="Selecione o código da filial"
                          options={[
                            { label: "Todas", value: null },
                            ...(selectPropsBranchOffice.options ?? []),
                          ]}
                        />
                      </Form.Item>
                    </div>
                  </div>
                </div>

                <div className="filter-fields-group-item">
                  <label>Status SIGO</label>
                  <Form.Item name="status">
                    <Select
                      options={STATUS_SIGO_LIST}
                      showSearch
                      placeholder="Selecione o status"
                      filterOption={(input, option) =>
                        (option?.label ?? "")
                          .toLowerCase()
                          .includes(input.toLowerCase())
                      }
                    />
                  </Form.Item>
                </div>

                <div className="filter-fields-group-item">
                  <label>Parceiro</label>
                  <Form.Item name="partner">
                    <Select
                      {...selectPropsPartners}
                      options={partnerOptions}
                      placeholder="Parceiro"
                    />
                  </Form.Item>
                </div>

                <div className="filter-fields-group-item">
                  <div className="group-state-city">
                    <div className="state-city-field">
                      <label>Cidade</label>
                      <Form.Item name="city">
                        <Input
                          className="input-custom"
                          placeholder="Digite a cidade"
                        />
                      </Form.Item>
                    </div>
                    <div className="state-city-field">
                      <label>Estados</label>
                      <Form.Item name="state" className="input-select">
                        <Select
                          placeholder="Selecione o estado"
                          options={[
                            { label: "Todos", value: null },
                            ...STATES_LIST_BRAZIL,
                          ]}
                          showSearch
                          filterOption={(input, option) =>
                            (option?.label ?? "")
                              .toLowerCase()
                              .includes(input.toLowerCase())
                          }
                        />
                      </Form.Item>
                    </div>
                  </div>
                </div>

                <div className="filter-fields-group-item">
                  <label>Última tela</label>
                  <Form.Item name="last_screen">
                    <Select
                      placeholder="Selecione a última tela"
                      options={[
                        { label: "Todas", value: null },
                        ...SCREEN_LIST,
                      ]}
                      showSearch
                      filterOption={(input, option) =>
                        (option?.label ?? "")
                          .toLowerCase()
                          .includes(input.toLowerCase())
                      }
                    />
                  </Form.Item>
                </div>

                <div className="filter-fields-group-item">
                  <div className="group-date">
                    <div className="group-label">
                      <label className="ant-col ant-form-item-label">
                        Data de Criação
                      </label>
                    </div>
                    <div className="group-fields">
                      <Form.Item
                        name="created_start"
                        getValueProps={(value) => ({
                          value: value ? dayjs(value).hour(12) : "",
                        })}
                        className="date-field"
                      >
                        <DatePicker
                          format="DD/MM/YYYY"
                          locale={ptBR}
                          disabledDate={(current) =>
                            current &&
                            current.isAfter(
                              dayjs(
                                searchFormProps?.form?.getFieldValue(
                                  "created_end",
                                ),
                              ).startOf("day"),
                              "day",
                            )
                          }
                          placeholder="Início"
                        />
                      </Form.Item>
                      <Form.Item
                        name="created_end"
                        getValueProps={(value) => ({
                          value: value ? dayjs(value).hour(12) : "",
                        })}
                        className="date-field"
                      >
                        <DatePicker
                          format="DD/MM/YYYY"
                          locale={ptBR}
                          disabledDate={(current) =>
                            current &&
                              searchFormProps?.form?.getFieldValue(
                                "created_start",
                              )
                              ? current.isBefore(
                                dayjs(
                                  searchFormProps?.form?.getFieldValue(
                                    "created_start",
                                  ),
                                ).startOf("day"),
                                "day",
                              )
                              : false
                          }
                          placeholder="Fim"
                        />
                      </Form.Item>
                    </div>
                  </div>
                </div>

                <div className="filter-fields-group-item">
                  <div className="group-date">
                    <div className="group-label">
                      <label className="ant-col ant-form-item-label">
                        Data da Última Atualização
                      </label>
                    </div>
                    <div className="group-fields">
                      <Form.Item
                        name="updated_start"
                        getValueProps={(value) => ({
                          value: value ? dayjs(value).hour(12) : "",
                        })}
                        className="date-field"
                      >
                        <DatePicker
                          format="DD/MM/YYYY"
                          locale={ptBR}
                          disabledDate={(current) =>
                            current &&
                            current.isAfter(
                              dayjs(
                                searchFormProps?.form?.getFieldValue(
                                  "updated_end",
                                ),
                              ).startOf("day"),
                              "day",
                            )
                          }
                          placeholder="Início"
                        />
                      </Form.Item>
                      <Form.Item
                        name="updated_end"
                        getValueProps={(value) => ({
                          value: value ? dayjs(value).hour(12) : "",
                        })}
                        className="date-field"
                      >
                        <DatePicker
                          format="DD/MM/YYYY"
                          locale={ptBR}
                          disabledDate={(current) =>
                            current &&
                              searchFormProps?.form?.getFieldValue(
                                "updated_start",
                              )
                              ? current.isBefore(
                                dayjs(
                                  searchFormProps?.form?.getFieldValue(
                                    "updated_start",
                                  ),
                                ).startOf("day"),
                                "day",
                              )
                              : false
                          }
                          placeholder="Fim"
                        />
                      </Form.Item>
                    </div>
                  </div>
                </div>

                <div className="filter-fields-group-item">
                  <div className="group-date">
                    <div className="group-label">
                      <label className="ant-col ant-form-item-label">
                        Data de Pagamento
                      </label>
                    </div>
                    <div className="group-fields">
                      <Form.Item
                        name="payment_start"
                        getValueProps={(value) => ({
                          value: value ? dayjs(value).hour(12) : "",
                        })}
                        className="date-field"
                      >
                        <DatePicker
                          format="DD/MM/YYYY"
                          locale={ptBR}
                          disabledDate={(current) =>
                            current &&
                            current.isAfter(
                              dayjs(
                                searchFormProps?.form?.getFieldValue(
                                  "payment_end",
                                ),
                              ).startOf("day"),
                              "day",
                            )
                          }
                          placeholder="Início"
                        />
                      </Form.Item>
                      <Form.Item
                        name="payment_end"
                        getValueProps={(value) => ({
                          value: value ? dayjs(value).hour(12) : "",
                        })}
                        className="date-field"
                      >
                        <DatePicker
                          format="DD/MM/YYYY"
                          locale={ptBR}
                          disabledDate={(current) =>
                            current &&
                              searchFormProps?.form?.getFieldValue(
                                "payment_start",
                              )
                              ? current.isBefore(
                                dayjs(
                                  searchFormProps?.form?.getFieldValue(
                                    "payment_start",
                                  ),
                                ).startOf("day"),
                                "day",
                              )
                              : false
                          }
                          placeholder="Fim"
                        />
                      </Form.Item>
                    </div>
                  </div>
                </div>

                <div className="filter-fields-group-item">
                  <div className="group-date">
                    <div className="group-label">
                      <label className="ant-col ant-form-item-label">
                        Data de Emissão de Boleto
                      </label>
                    </div>
                    <div className="group-fields">
                      <Form.Item
                        name="bill_emission_start"
                        getValueProps={(value) => ({
                          value: value ? dayjs(value).hour(12) : "",
                        })}
                        className="date-field"
                      >
                        <DatePicker
                          format="DD/MM/YYYY"
                          locale={ptBR}
                          disabledDate={(current) =>
                            current &&
                            current.isAfter(
                              dayjs(
                                searchFormProps?.form?.getFieldValue(
                                  "bill_emission_end",
                                ),
                              ).startOf("day"),
                              "day",
                            )
                          }
                          placeholder="Início"
                        />
                      </Form.Item>
                      <Form.Item
                        name="bill_emission_end"
                        getValueProps={(value) => ({
                          value: value ? dayjs(value).hour(12) : "",
                        })}
                        className="date-field"
                      >
                        <DatePicker
                          format="DD/MM/YYYY"
                          locale={ptBR}
                          disabledDate={(current) =>
                            current &&
                              searchFormProps?.form?.getFieldValue(
                                "bill_emission_start",
                              )
                              ? current.isBefore(
                                dayjs(
                                  searchFormProps?.form?.getFieldValue(
                                    "bill_emission_start",
                                  ),
                                ).startOf("day"),
                                "day",
                              )
                              : false
                          }
                          placeholder="Fim"
                        />
                      </Form.Item>
                    </div>
                  </div>
                </div>
              </div>
              <div className="buttons-container">
                <Button
                  type="default"
                  icon={<Icons.ClearOutlined />}
                  className="ant-btn ant-btn-default"
                  onClick={handleClearFilters}
                >
                  Limpar
                </Button>
                <Button
                  type="primary"
                  icon={<Icons.SearchOutlined />}
                  className="ant-btn ant-btn-primary"
                  onClick={handleSubmitForm}
                >
                  Aplicar
                </Button>
              </div>
            </div>
          </Form>
        </ConfigProvider>
      </Card>
      <div className="budgets-table-wrapper">
        <Table
          {...tableProps}
          dataSource={showData ? tableProps.dataSource : []}
          loading={showData ? tableProps.loading : false}
          className="budgets-table"
          scroll={{
            y: 300,
          }}
          tableLayout="auto"
          pagination={
            showData ? {
              ...tableProps.pagination,
              position: ["bottomRight"],
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} de ${total} registros`,
            } : false
          }
          rowKey="_id"
          columns={columns}
        />
      </div>
    </List>
  );
};
